import React, {useEffect, useRef, useState} from 'react';
import {useLocation} from 'react-router-dom';
import {
    IGlobalStorefrontSettings,
    IStorefrontQuestion,
} from '../../models/Storefront';
import {
    useCreateStorefrontQuestionMutation, useDeleteStorefrontQuestionMutation,
    useGetStorefrontQuestionsQuery,
    useSaveStorefrontQuestionMutation
} from "../../store/endpoints/StorefrontQuestionEndpoints";
import StorefrontQuestionEditor from "./StorefrontQuestionEditor";
import {
    CommandBar,
    DefaultButton, Dialog, DialogFooter, DialogType,
    ICommandBarItemProps,
    IconButton,
    IContextualMenuProps,
    MessageBar,
    MessageBarType,
    Panel,
    PanelType,
    PrimaryButton,
    Spinner,
    SpinnerSize,
    Stack
} from "@fluentui/react";
import DataGrid from "../DataGrid/DataGrid";
import {GridSelectionMode, IColumnDetails} from "../../models/GridConfiguration";

interface IStorefrontGlobalQuestionsFormProps {
    globalSettings?: IGlobalStorefrontSettings;
}

const StorefrontGlobalQuestionsForm: React.FC<IStorefrontGlobalQuestionsFormProps> = (props) => {
    
    const globalQuestions =  useGetStorefrontQuestionsQuery('global')
    const [saver] = useSaveStorefrontQuestionMutation();
    const [createNewQuestion] = useCreateStorefrontQuestionMutation();
    const [saving, setSaving] = useState(false);
    const bottomRef = useRef<HTMLDivElement | null>(null);
    const [questionEditorError, setQuestionEditorError] = useState<string>();
    const [error, setError] = useState<string>();
    const [showEditor, setShowEditor] = useState(false);
    const [showConfirmDelete, setShowConfirmDelete] = useState(false);
    const [deleter] = useDeleteStorefrontQuestionMutation()
    const [selectedQuestion, setSelectedQuestion] = useState<IStorefrontQuestion>();

    /*
     * A per-question deep link names the question inside the setting id
     * (`storefront.global.orderQuestion.<questionId>.<field>`), so open that question's editor
     * rather than leaving the panel closed.
     */
    const location = useLocation();
    const focusedQuestionId = React.useMemo(() => {
        const focus = new URLSearchParams(location.search).get('focus');
        return /^storefront\.global\.orderQuestion\.([^.]+)\./.exec(focus ?? '')?.[1];
    }, [location.search]);
    useEffect(() => {
        if (!focusedQuestionId) return;
        const match = globalQuestions.data?.items.find((q) => q.id === focusedQuestionId);
        if (match) {
            setSelectedQuestion(match);
            setShowEditor(true);
        }
    }, [focusedQuestionId, globalQuestions.data]);

    const scrollToBottom = () => {
        bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    };
    
    const getNewQuestionId = () => {
        const sequence = (globalQuestions.data?.items.length ?? 0) + 1;
        
        const id = `Question-${sequence}`;
        
        if(globalQuestions.data?.items.findIndex(a => a.identifier == id)){
            return crypto.randomUUID()
        }
        return id;
    }
    
    const headerCommands: ICommandBarItemProps[] = [
        {
            key: 'addQuestion',
            text: 'Add Question',
            title: 'Add Question',
            disabled: saving,
            iconProps: {iconName: 'Add'},
            onClick: () => {

                const newQuestion = {
                    identifier: getNewQuestionId(),
                    questionText: '',
                    answers: [],
                    entityType: 'storefront',
                    entityId: 'global'
                }
                setSaving(true);
                createNewQuestion({question: newQuestion, storefront: 'global'})
                    .unwrap()
                    .then((r) => {
                        setSaving(false);
                        scrollToBottom()
                        setSelectedQuestion({...newQuestion, id: r.id })
                        setShowEditor(true)
                    })
                    .catch((error) => {
                        // Error: error contains the error object
                        setError(error?.data?.message ?? 'Unknown error creating question');
                        setSaving(false);
                    });
                
            }
        }
    ]

    const menuProps: IContextualMenuProps = {
        items: [
            {
                key: 'saveAndClose',
                text: 'Save and Close',
                iconProps: { iconName: 'SaveAndClose' },
                onClick: () => {
                    save();
                    setSelectedQuestion(undefined);
                    setShowEditor(false);
                }
            }
        ],
    };
    
    const footer = () => (
        <div style={{background: "white"}}>
            <Stack horizontal tokens={{childrenGap: '1em'}}>
                <PrimaryButton split menuProps={menuProps} text='Save' onClick={() => save()} disabled={saving} />
                <DefaultButton 
                    text='Cancel' 
                    onClick={() => {
                        setSelectedQuestion(undefined)
                        setShowEditor(false)
                        setSaving(false)
                    }}
                    disabled={saving} />
            </Stack>
        </div>
    )

    const header = () => (
        <div style={{flexGrow: 1, paddingLeft: '25px', background: "white"}}>
            <h1>Edit Storefront Question</h1>
            {questionEditorError &&
                <MessageBar messageBarType={MessageBarType.error}>{questionEditorError}</MessageBar>
            }
        </div>
    )
    
    const save = () => {
        setSaving(true)
        if(selectedQuestion?.id) {
            saver({question: selectedQuestion, storefront: 'global', questionId: selectedQuestion.id}).unwrap()
                .then((e) => {
                    if(e.success == false){
                        setQuestionEditorError(e.message ?? 'Unknown error saving question')
                    }
                    setSaving(false)
                })
                .catch((error) => {
                    // Error: error contains the error object
                    setQuestionEditorError(error?.data?.message ?? 'Unknown error creating question');
                    setSaving(false);
                });
        }
    }
    
    const deleteQuestion = () => {

        if(selectedQuestion?.id) {
            deleter({storefront: 'global', questionId: selectedQuestion.id})
        }
        
        setSelectedQuestion(undefined)
    }

    const getCustomColumns = () => {
        const columnDetails: IColumnDetails[] = [
            {
                index: 0,
                identifier: 'Details',
                label: '',
                type: '',
                effectiveWidth: 50,
                component:(q: IStorefrontQuestion) =>
                    <IconButton data-testid='edit-global-question' iconProps={{iconName: 'edit'}} onClick={() => {
                        setSelectedQuestion(q)
                        setShowEditor(true)
                    }}/>,
                required: true,
            },
            {
                identifier: 'identifier',
                label: 'Identifier',
                type: '',
                sortable: false,
                filterable: false,
                effectiveWidth: 200,
                required: true,
                component:(q: IStorefrontQuestion) => <div>{q.identifier}</div>,
            },
            {
                identifier: 'questionText',
                label: 'Question Text',
                type: '',
                sortable: false,
                filterable: false,
                required: true,    
                component:(q: IStorefrontQuestion) => <div>{q.questionText}</div>,

            },
            {
                identifier: 'required',
                label: 'Required',
                type: '',
                sortable: false,
                filterable: false,
                required: true,
                component:(q: IStorefrontQuestion) => <div>{q.isRequired ? ('True') : ('False')}</div>,
            },
            {
                identifier: 'Delete',
                label: '',
                type: '',
                effectiveWidth: 50,
                component:(q: IStorefrontQuestion) =>
                    <IconButton data-testid='delete-global-question' iconProps={{iconName: 'Delete'}} onClick={() => {
                        setSelectedQuestion(q)
                        setShowConfirmDelete(true)
                    }}/>,
                required: true,
            },
        ];
        return columnDetails
    }

    return (
        <div>
            <Dialog
                hidden={!showConfirmDelete}
                onDismiss={() => setShowConfirmDelete(false)}
                dialogContentProps={{
                    type: DialogType.largeHeader,
                    title: 'Delete Question',
                    subText: 'Are you sure you want to delete this question? This question will be removed from all storefronts.',
                }}
                modalProps={{
                    isBlocking: true,
                }}
            >
                <DialogFooter>
                    <DefaultButton
                        primary
                        text='Delete'
                        onClick={() => {
                            deleteQuestion()
                            setShowConfirmDelete(false)
                        }}
                    />
                    <DefaultButton
                        text='Cancel'
                        onClick={() => setShowConfirmDelete(false)}
                    />
                </DialogFooter>
            </Dialog>
            <Panel
                onOuterClick={() => {console.log("swallowing modal close event")}}
                type={PanelType.large}
                headerText={'Edit Storefront Question'}
                isOpen={showEditor}
                onDismiss={() => setShowEditor(false)}
                closeButtonAriaLabel='Close'
                isFooterAtBottom={true}
                onRenderFooterContent={footer}
                onRenderHeader={header}
                styles={{
                    commands: {
                        background: "white"
                    },
                    footer: {
                        background: "white"
                    },
                    navigation: {
                        flex: '1 1 auto',
                    },
                }}
            >
                <StorefrontQuestionEditor
                    question={selectedQuestion}
                    canEdit={true}
                    settingKeyPrefix='storefront.global.orderQuestion'
                    onChange={(q) => {
                        setSelectedQuestion({...q})
                    }}
                />
                
            </Panel>
            {(globalQuestions.isFetching || globalQuestions.isLoading) ? <Spinner label={'Loading Questions...'} size={SpinnerSize.large}/> :
            <React.Fragment>
                {error &&
                    <MessageBar messageBarType={MessageBarType.error}>{error}</MessageBar>
                }
                <CommandBar items={headerCommands} farItems={[]}/>
                
                <DataGrid
                    getItemId={(item: IStorefrontQuestion) => item.id ?? ''} 
                    items={globalQuestions.data?.items ?? []}
                    selectionMode={GridSelectionMode.none}
                    loading={globalQuestions.isFetching || globalQuestions.isLoading}
                    overrideColumns={getCustomColumns()}
                    useContentHeight={true}
                    />

            </React.Fragment>
            }
            <div ref={bottomRef} />
        </div>
    );
}

export default StorefrontGlobalQuestionsForm;